import type { RenderContext, SectionProps } from "../context";
import { field, titleId } from "../context";
import { Ambient, Balloons, Confetti, Sparkles, Squiggle } from "../decor";
import { parseEventDate, socialLinks } from "../helpers";
import { Icon } from "../icons";
import { ambientOf } from "../theme";
import {
  Container,
  CtaButton,
  Heading,
  EditorImagePlaceholder,
  Eyebrow,
  Img,
  MultiLine,
  Ornament,
  SectionShell,
  ctaVisible,
  SiteLink,
  cx,
  enterDelay,
  headingTag,
  isPriority,
  TitleText,
  Marquee as UiMarquee,
  Slideshow as UiSlideshow,
  type ButtonStyle,
} from "../ui";

type HeroNode = SectionProps<"hero">["node"];

function HeroTitle({ ctx, node, className }: { ctx: RenderContext; node: HeroNode; className: string }) {
  return (
    <Heading
      level={headingTag(ctx, node)}
      id={titleId(ctx, node)}
      className={cx("baze-heading baze-enter text-balance", className)}
      style={enterDelay(1)}
      {...field(ctx, "content.title")}
    >
      <TitleText ctx={ctx} node={node} text={node.content.title} />
    </Heading>
  );
}

function HeroEyebrow({ ctx, node, className }: { ctx: RenderContext; node: HeroNode; className?: string }) {
  if (!node.content.eyebrow) return null;
  return (
    <div className="baze-enter" style={enterDelay(0)}>
      <Eyebrow ctx={ctx} text={node.content.eyebrow} path="content.eyebrow" className={className} />
    </div>
  );
}

function HeroDescription({
  ctx,
  node,
  className,
}: {
  ctx: RenderContext;
  node: HeroNode;
  className?: string;
}) {
  if (!node.content.description) return null;
  return (
    <div className="baze-enter" style={enterDelay(2)}>
      <MultiLine ctx={ctx} text={node.content.description} path="content.description" className={className} />
    </div>
  );
}

function HeroActions({
  ctx,
  node,
  center,
  secondary = "secondary",
}: {
  ctx: RenderContext;
  node: HeroNode;
  center?: boolean;
  secondary?: ButtonStyle;
}) {
  const c = node.content;
  // Live sites drop buttons without a working link; drop the spacing wrapper with them.
  if (!ctaVisible(ctx, c.primaryCta) && !ctaVisible(ctx, c.secondaryCta)) return null;
  return (
    <div
      className={cx(
        "baze-enter mt-8 flex flex-col gap-3 @sm:flex-row @sm:flex-wrap",
        center && "@sm:justify-center",
      )}
      style={enterDelay(3)}
    >
      <CtaButton ctx={ctx} cta={c.primaryCta} path="content.primaryCta" style="primary" />
      <CtaButton ctx={ctx} cta={c.secondaryCta} path="content.secondaryCta" style={secondary} withArrow />
    </div>
  );
}

function Locale({ ctx, light, center }: { ctx: RenderContext; light?: boolean; center?: boolean }) {
  const city = ctx.site.contact.city;
  if (!city) return null;
  return (
    <p
      className={cx(
        "baze-enter mt-6 flex items-center gap-1.5 text-sm",
        light ? "text-white/80" : "text-site-muted",
        center && "justify-center",
      )}
      style={enterDelay(4)}
    >
      <Icon name="map-pin" className="size-4" />
      <span>{city}</span>
    </p>
  );
}

/**
 * Soft blurred colour fields behind light heroes. Decorative; stays inside the section, so its clipped edge
 * reads as a band: heroes that use it (or the grid) are `baze-band` and keep their own padding.
 */
function Glow({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cx("pointer-events-none absolute inset-0 -z-10 overflow-hidden", className)}
    >
      <div className="absolute -top-32 -right-24 size-[28rem] rounded-full bg-site-secondary/25 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 size-[26rem] rounded-full bg-site-primary/10 blur-3xl" />
    </div>
  );
}

function Centered({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Glow />
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
          <HeroTitle
            ctx={ctx}
            node={node}
            className="text-[2.5rem] leading-[1.05] @md:text-5xl @3xl:text-6xl @5xl:text-[4.25rem]"
          />
          <HeroDescription
            ctx={ctx}
            node={node}
            className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl"
          />
          <HeroActions ctx={ctx} node={node} center />
          <Locale ctx={ctx} center />
        </div>
        {c.image ? (
          <div
            className="baze-enter-pop mt-12 aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @3xl:mt-16 @3xl:aspect-[21/9]"
            style={enterDelay(3)}
          >
            <Img
              ctx={ctx}
              image={c.image}
              sizes="(min-width: 1200px) 1120px, 100vw"
              priority={isPriority(ctx, node)}
              path="content.image"
              target={1600}
            />
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

function Split({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Glow />
      <Container>
        <div
          className={cx(
            "grid items-center gap-12 @4xl:gap-14 @5xl:gap-20",
            hasImage && "@4xl:grid-cols-[1.05fr_1fr]",
          )}
        >
          <div className={cx(!hasImage && "max-w-3xl")}>
            <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
            <HeroTitle
              ctx={ctx}
              node={node}
              className="text-[2.5rem] leading-[1.05] @md:text-5xl @5xl:text-[3.75rem]"
            />
            <HeroDescription
              ctx={ctx}
              node={node}
              className="mt-6 max-w-xl text-lg leading-relaxed text-site-muted"
            />
            <HeroActions ctx={ctx} node={node} />
            <Locale ctx={ctx} />
          </div>
          {hasImage ? (
            <div className="baze-enter-pop relative" style={enterDelay(2)}>
              <div
                aria-hidden="true"
                className="absolute -inset-3 -z-0 rotate-2 rounded-site-lg bg-site-secondary/25 @3xl:-inset-4"
              />
              <div
                aria-hidden="true"
                className="baze-float absolute -top-6 -left-6 hidden size-24 rounded-full border border-site-primary/25 @4xl:block"
              />
              <div className="relative aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @4xl:aspect-[4/5]">
                {c.image ? (
                  <Img
                    ctx={ctx}
                    image={c.image}
                    sizes="(min-width: 1024px) 560px, 100vw"
                    priority={isPriority(ctx, node)}
                    path="content.image"
                    target={1600}
                  />
                ) : (
                  <EditorImagePlaceholder ctx={ctx} path="content.image" />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </Container>
    </SectionShell>
  );
}

function ImageBackground(props: SectionProps<"hero">) {
  const { node, ctx } = props;
  const c = node.content;
  if (!c.image) return <Centered {...props} />;
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className="baze-hero relative isolate overflow-hidden bg-site-inverted!"
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="baze-kenburns h-full w-full">
          <Img
            ctx={ctx}
            image={c.image}
            sizes="100vw"
            priority={isPriority(ctx, node)}
            path="content.image"
            target={1600}
          />
        </div>
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-linear-to-t from-black/85 via-black/50 to-black/20 @3xl:bg-linear-to-r @3xl:from-black/80 @3xl:via-black/45 @3xl:to-black/10"
      />
      <Container className="flex min-h-[34rem] items-end py-(--site-section-y) @3xl:min-h-[40rem] @3xl:items-center @3xl:py-(--site-section-y-lg) @5xl:min-h-[44rem]">
        <div className="max-w-2xl text-white">
          <HeroEyebrow ctx={ctx} node={node} className="mb-5 text-white/85!" />
          <HeroTitle
            ctx={ctx}
            node={node}
            className="text-[2.5rem] leading-[1.05] text-white @md:text-5xl @3xl:text-6xl @5xl:text-[4.25rem]"
          />
          <HeroDescription
            ctx={ctx}
            node={node}
            className="mt-6 max-w-xl text-lg leading-relaxed text-white/85 @3xl:text-xl"
          />
          <HeroActions ctx={ctx} node={node} secondary="glass" />
          <Locale ctx={ctx} light />
        </div>
      </Container>
      <div
        aria-hidden="true"
        className="baze-enter absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/70 @3xl:flex"
        style={enterDelay(6)}
      >
        <span className="text-[0.6875rem] font-semibold tracking-[0.3em] uppercase">Gulir</span>
        <span className="baze-float block h-10 w-px bg-white/50" />
      </div>
    </SectionShell>
  );
}

/** Full-bleed photo slider behind the hero copy. The slides are `image` followed by `images`. */
function Slideshow(props: SectionProps<"hero">) {
  const { node, ctx } = props;
  const c = node.content;
  const slides = [
    ...(c.image ? [{ image: c.image, path: "content.image" }] : []),
    ...(c.images ?? []).map((image, i) => ({ image, path: `content.images.${i}` })),
  ];
  if (slides.length < 2) return <ImageBackground {...props} />;
  const priority = isPriority(ctx, node);
  return (
    <SectionShell ctx={ctx} node={node} padded={false} className="baze-hero relative isolate overflow-hidden bg-site-inverted!">
      <UiSlideshow
        ctx={ctx}
        tone="light"
        pauseOnHover={false}
        label={`Foto: ${c.title}`}
        className="absolute inset-0 -z-10"
        trackClassName="h-full"
        controlsClassName="absolute right-5 bottom-6 z-10 @3xl:right-8 @3xl:bottom-8"
        slides={slides.map((s, i) => (
          <>
            <Img ctx={ctx} image={s.image} sizes="100vw" priority={priority && i === 0} path={s.path} target={1600} />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/85 via-black/50 to-black/20 @3xl:bg-linear-to-r @3xl:from-black/80 @3xl:via-black/45 @3xl:to-black/10"
            />
          </>
        ))}
      />
      {/* Copy floats over the slider; only its links and buttons take the pointer, so swipes reach the photos. */}
      <Container className="pointer-events-none flex min-h-[34rem] items-end pt-(--site-section-y) pb-24 @3xl:min-h-[40rem] @3xl:items-center @3xl:py-(--site-section-y-lg) @5xl:min-h-[44rem] [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
        <div className="max-w-2xl text-white">
          <HeroEyebrow ctx={ctx} node={node} className="mb-5 text-white/85!" />
          <HeroTitle ctx={ctx} node={node} className="text-[2.5rem] leading-[1.05] text-white @md:text-5xl @3xl:text-6xl @5xl:text-[4.25rem]" />
          <HeroDescription ctx={ctx} node={node} className="mt-6 max-w-xl text-lg leading-relaxed text-white/85 @3xl:text-xl" />
          <HeroActions ctx={ctx} node={node} secondary="glass" />
          <Locale ctx={ctx} light />
        </div>
      </Container>
    </SectionShell>
  );
}

function Minimal({ node, ctx }: SectionProps<"hero">) {
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div
        aria-hidden="true"
        className="baze-grid-lines pointer-events-none absolute inset-0 -z-10 text-site-text [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]"
      />
      <Container>
        <HeroEyebrow ctx={ctx} node={node} className="mb-6" />
        <HeroTitle
          ctx={ctx}
          node={node}
          className="max-w-5xl text-[2.75rem] leading-[1.02] @md:text-6xl @3xl:text-7xl @5xl:text-[5.5rem]"
        />
        <div className="mt-10 grid gap-8 border-t border-site-border pt-8 @4xl:grid-cols-[1.2fr_1fr] @4xl:items-end">
          <HeroDescription
            ctx={ctx}
            node={node}
            className="max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl"
          />
          <div className="@4xl:justify-self-end">
            <HeroActions ctx={ctx} node={node} />
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/** Arched portrait photo with a hairline frame: weddings, salons, villas. */
function Arch({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Glow />
      <Container>
        <div className={cx("grid items-center gap-12 @4xl:gap-20", hasImage && "@4xl:grid-cols-[1fr_1.1fr]")}>
          {hasImage ? (
            <div
              className="baze-enter-pop relative mx-auto w-full max-w-[16.5rem] @md:max-w-[20rem] @4xl:order-2 @4xl:max-w-[28rem]"
              style={enterDelay(1)}
            >
              <div
                aria-hidden="true"
                className="absolute -inset-3 rounded-t-full rounded-b-site-lg border border-site-primary/35 @3xl:-inset-4"
              />
              <div
                aria-hidden="true"
                className="baze-spin-slow absolute -top-8 -right-8 hidden size-28 rounded-full border border-dashed border-site-primary/30 @3xl:block"
              />
              <div className="relative aspect-[3/4] overflow-hidden rounded-t-full rounded-b-site-lg bg-site-surface shadow-site-lg">
                {c.image ? (
                  <Img
                    ctx={ctx}
                    image={c.image}
                    sizes="(min-width: 1024px) 448px, 352px"
                    priority={isPriority(ctx, node)}
                    path="content.image"
                    target={960}
                  />
                ) : (
                  <EditorImagePlaceholder ctx={ctx} path="content.image" />
                )}
              </div>
              <span
                aria-hidden="true"
                className="absolute -bottom-5 left-1/2 grid size-11 -translate-x-1/2 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md"
              >
                <Icon name="sparkle" className="size-5" />
              </span>
            </div>
          ) : null}
          <div
            className={cx("text-center @4xl:text-left", !hasImage && "mx-auto max-w-3xl @4xl:text-center")}
          >
            <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
            <HeroTitle
              ctx={ctx}
              node={node}
              className="text-[2.5rem] leading-[1.08] @md:text-5xl @5xl:text-[4rem]"
            />
            <div
              className={cx("baze-enter mt-6 flex justify-center", hasImage && "@4xl:justify-start")}
              style={enterDelay(2)}
            >
              <Ornament className="w-48" />
            </div>
            <HeroDescription
              ctx={ctx}
              node={node}
              className={cx(
                "mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted",
                hasImage && "@4xl:mx-0",
              )}
            />
            <HeroActions ctx={ctx} node={node} center={!hasImage} />
            <Locale ctx={ctx} center={!hasImage} />
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/** Magazine layout: oversized headline, copy column, then a wide photo with a caption chip. */
function Editorial({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const city = ctx.site.contact.city;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero relative isolate overflow-hidden">
      <Container size="wide">
        <div className="grid gap-8 @4xl:grid-cols-[1.6fr_1fr] @4xl:items-end @4xl:gap-14">
          <div>
            <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
            <HeroTitle
              ctx={ctx}
              node={node}
              className="text-[2.75rem] leading-[1] @md:text-6xl @3xl:text-7xl @6xl:text-[5.75rem]"
            />
          </div>
          <div className="@4xl:pb-2">
            <HeroDescription ctx={ctx} node={node} className="text-lg leading-relaxed text-site-muted" />
            <HeroActions ctx={ctx} node={node} />
          </div>
        </div>
        {c.image || ctx.editor ? (
          <div className="baze-enter-pop relative mt-12 @3xl:mt-16" style={enterDelay(3)}>
            <div className="aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @3xl:aspect-[21/9]">
              {c.image ? (
                <div className="baze-kenburns h-full w-full">
                  <Img
                    ctx={ctx}
                    image={c.image}
                    sizes="(min-width: 1280px) 1216px, 100vw"
                    priority={isPriority(ctx, node)}
                    path="content.image"
                    target={1600}
                  />
                </div>
              ) : (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              )}
            </div>
            {city ? (
              <p className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-site-bg/90 px-4 py-2 text-sm font-semibold text-site-text shadow-site-md backdrop-blur @3xl:bottom-6 @3xl:left-6">
                <Icon name="map-pin" className="size-4 text-site-primary" />
                {city}
              </p>
            ) : null}
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

/** Link-in-bio header: round avatar, name, short bio, social icons. Compact so links sit above the fold. */
function Profile({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const socials = socialLinks(ctx.site);
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className="baze-hero relative isolate overflow-hidden pt-12 pb-6 @3xl:pt-16 @3xl:pb-8"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-56 bg-linear-to-b from-site-secondary/35 to-transparent"
      />
      <Container size="narrow" className="text-center">
        {c.image || ctx.editor ? (
          <div className="baze-enter-pop relative mx-auto size-28 @3xl:size-32" style={enterDelay(0)}>
            <div
              aria-hidden="true"
              className="baze-spin-slow absolute -inset-1.5 rounded-full border-2 border-dashed border-site-primary/40"
            />
            <div className="relative size-full overflow-hidden rounded-full bg-site-surface shadow-site-md ring-4 ring-site-bg">
              {c.image ? (
                <Img
                  ctx={ctx}
                  image={c.image}
                  sizes="128px"
                  target={480}
                  priority={isPriority(ctx, node)}
                  path="content.image"
                />
              ) : (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              )}
            </div>
          </div>
        ) : null}
        <HeroEyebrow ctx={ctx} node={node} className="mt-6" />
        <HeroTitle ctx={ctx} node={node} className="mt-3 text-[2rem] leading-tight @3xl:text-[2.5rem]" />
        <HeroDescription
          ctx={ctx}
          node={node}
          className="mx-auto mt-3 max-w-md text-base leading-relaxed text-site-muted"
        />
        {socials.length > 0 ? (
          <ul className="baze-enter mt-5 flex justify-center gap-2" style={enterDelay(3)}>
            {socials.map((s) => (
              <li key={s.kind}>
                <SiteLink
                  ctx={ctx}
                  href={s.href}
                  trackLink
                  ariaLabel={`${s.label} ${ctx.site.name}`}
                  className="grid size-11 place-items-center rounded-full border border-site-border bg-site-surface text-site-text transition hover:-translate-y-0.5 hover:border-site-primary hover:text-site-primary motion-reduce:transition-none"
                >
                  <Icon name={s.kind} className="size-[1.125rem]" />
                </SiteLink>
              </li>
            ))}
          </ul>
        ) : null}
        <HeroActions ctx={ctx} node={node} center />
      </Container>
    </SectionShell>
  );
}

/**
 * Party hero for birthdays, launches and festive promos: pastel glow, falling confetti, balloons and
 * sparkles (CSS only), an optional round photo with a waving party badge, and a hand-drawn squiggle.
 */
function Celebration({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(55% 45% at 15% 10%, color-mix(in srgb, var(--site-secondary) 55%, transparent), transparent 70%), radial-gradient(50% 45% at 90% 15%, color-mix(in srgb, var(--site-accent) 35%, transparent), transparent 70%), radial-gradient(60% 50% at 50% 100%, color-mix(in srgb, var(--site-primary) 14%, transparent), transparent 70%)",
        }}
      />
      <Confetti className="-z-10" />
      <Balloons className="-z-10" />
      <Container size="narrow" className="relative text-center">
        {c.image || ctx.editor ? (
          <div className="baze-enter-pop relative mx-auto mb-8 size-36 @3xl:size-44" style={enterDelay(0)}>
            <div
              aria-hidden="true"
              className="baze-spin-slow absolute -inset-2.5 rounded-full border-[3px] border-dashed border-site-accent/70"
            />
            <div className="relative size-full overflow-hidden rounded-full bg-site-surface shadow-site-lg ring-[6px] ring-site-bg">
              {c.image ? (
                <Img
                  ctx={ctx}
                  image={c.image}
                  sizes="176px"
                  target={480}
                  priority={isPriority(ctx, node)}
                  path="content.image"
                />
              ) : (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              )}
            </div>
            <span
              aria-hidden="true"
              className="baze-wiggle absolute -top-1 -right-2 grid size-12 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md @3xl:size-14"
            >
              <Icon name="party" className="size-6 @3xl:size-7" />
            </span>
          </div>
        ) : null}
        <div className="relative">
          <Sparkles className="-inset-x-6 -inset-y-4" />
          <div className="baze-enter" style={enterDelay(1)}>
            <Eyebrow
              ctx={ctx}
              text={c.eyebrow}
              path="content.eyebrow"
              className="mb-5 inline-flex rounded-full bg-site-surface/85 px-4 py-1.5 tracking-[0.14em] shadow-site-sm ring-1 ring-site-border"
            />
          </div>
          <HeroTitle
            ctx={ctx}
            node={node}
            className="text-[2.75rem] leading-[1.02] @md:text-6xl @3xl:text-7xl"
          />
          <div className="baze-enter mt-4" style={enterDelay(2)}>
            <Squiggle />
          </div>
        </div>
        <HeroDescription
          ctx={ctx}
          node={node}
          className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl"
        />
        <HeroActions ctx={ctx} node={node} center />
        <Locale ctx={ctx} center />
      </Container>
    </SectionShell>
  );
}

/**
 * Invitation cover: a full-screen opening with the couple's names, "Kepada Yth." and the guest's name.
 * The guest name is a placeholder ("Tamu Undangan"); on live sites the runtime fills it from the link's
 * `?to=` parameter (personal links from the dashboard). The primary button opens the invitation (scrolls
 * to the content). Photo covers get a dark overlay; without a photo, a soft glow. The theme's ambient particles (petals by default) drift down.
 */
function Cover({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const photo = !!c.image;
  const en = ctx.site.locale === "en-US";
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className={cx(
        "baze-hero baze-band relative isolate flex items-center overflow-hidden py-20 text-center",
        ctx.editor || ctx.thumbnail ? "min-h-[40rem]" : "min-h-[100svh]",
        photo && "bg-site-inverted!",
      )}
    >
      {photo ? (
        <>
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="baze-kenburns h-full w-full">
              <Img
                ctx={ctx}
                image={c.image!}
                sizes="100vw"
                priority={isPriority(ctx, node)}
                path="content.image"
                target={1600}
              />
            </div>
          </div>
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-linear-to-b from-black/55 via-black/45 to-black/75"
          />
        </>
      ) : (
        <Glow />
      )}
      <Ambient kind={ambientOf(ctx.theme)} className="-z-10" />
      <Container size="narrow" className={cx("relative", photo ? "text-white" : "text-site-text")}>
        <HeroEyebrow
          ctx={ctx}
          node={node}
          className={cx("mb-6 tracking-[0.3em]", photo && "text-white/85!")}
        />
        <HeroTitle
          ctx={ctx}
          node={node}
          className={cx("text-[2.75rem] leading-[1.05] @md:text-6xl @3xl:text-7xl", photo && "text-white")}
        />
        <div className="baze-enter mt-6" style={enterDelay(2)}>
          <Ornament className={cx("mx-auto", photo && "text-white/70!")} />
        </div>
        <HeroDescription
          ctx={ctx}
          node={node}
          className={cx(
            "mx-auto mt-6 max-w-lg text-lg leading-relaxed",
            photo ? "text-white/85" : "text-site-muted",
          )}
        />
        <CoverGuest ctx={ctx} light={photo} className="mt-10" />
        <HeroActions ctx={ctx} node={node} center secondary={photo ? "glass" : "secondary"} />
      </Container>
      <div
        aria-hidden="true"
        className={cx(
          "baze-enter absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2",
          photo ? "text-white/70" : "text-site-muted",
        )}
        style={enterDelay(6)}
      >
        <span className="text-[0.6875rem] font-semibold tracking-[0.3em] uppercase">
          {en ? "Scroll" : "Gulir"}
        </span>
        <span className={cx("baze-float block h-10 w-px", photo ? "bg-white/50" : "bg-site-border")} />
      </div>
    </SectionShell>
  );
}

/**
 * Kinetic poster: an edge-to-edge headline, a primary-colour ticker repeating the eyebrow (or the site
 * name), then copy and buttons in two columns above a wide photo. The ticker is decorative.
 */
function Marquee({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const word = c.eyebrow || ctx.site.name;
  const run = (
    <span className="flex shrink-0 items-center gap-6 pr-6 whitespace-nowrap @3xl:gap-10 @3xl:pr-10">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="flex items-center gap-6 @3xl:gap-10">
          <span>{word}</span>
          <Icon name="sparkle" className="size-5 shrink-0 @3xl:size-7" />
        </span>
      ))}
    </span>
  );
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Container size="wide">
        <HeroEyebrow ctx={ctx} node={node} className="mb-6" />
        <HeroTitle
          ctx={ctx}
          node={node}
          className="max-w-[18ch] text-[2.5rem] leading-[0.98] @md:text-6xl @3xl:text-7xl @5xl:text-[6.5rem]"
        />
      </Container>
      <div aria-hidden="true" className="baze-enter -mx-4 mt-10 -rotate-1 @3xl:mt-14" style={enterDelay(2)}>
        <UiMarquee
          seconds={Math.min(60, Math.max(20, word.length * 1.2))}
          repeat={run}
          className="baze-heading border-y-2 border-site-text bg-site-primary py-3 text-2xl leading-none text-site-on-primary @3xl:py-4 @3xl:text-4xl"
        >
          {run}
        </UiMarquee>
      </div>
      <Container size="wide" className="mt-10 @3xl:mt-14">
        <div className="grid gap-6 @4xl:grid-cols-[1.2fr_1fr] @4xl:items-end @4xl:gap-14">
          <HeroDescription
            ctx={ctx}
            node={node}
            className="max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl"
          />
          <div className="@4xl:justify-self-end">
            <HeroActions ctx={ctx} node={node} />
            <Locale ctx={ctx} />
          </div>
        </div>
        {c.image || ctx.editor ? (
          <div
            className="baze-enter-pop mt-12 aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @3xl:mt-16 @3xl:aspect-[21/9]"
            style={enterDelay(4)}
          >
            {c.image ? (
              <div className="baze-kenburns h-full w-full">
                <Img
                  ctx={ctx}
                  image={c.image}
                  sizes="(min-width: 1280px) 1216px, 100vw"
                  priority={isPriority(ctx, node)}
                  path="content.image"
                  target={1600}
                />
              </div>
            ) : (
              <EditorImagePlaceholder ctx={ctx} path="content.image" />
            )}
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

/**
 * Scrapbook / Bauhaus collage: the photo as a tilted print with tape corners, geometric shapes in the
 * secondary and accent colours, and a spinning sticker. Copy sits beside it like Split.
 */
function Collage({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div
        aria-hidden="true"
        className="baze-dots pointer-events-none absolute inset-0 -z-10 text-site-text opacity-60 [mask-image:radial-gradient(ellipse_at_70%_40%,black,transparent_70%)]"
      />
      <Container>
        <div className={cx("grid items-center gap-14 @4xl:gap-16", hasImage && "@4xl:grid-cols-[1fr_1fr]")}>
          <div className={cx(!hasImage && "max-w-3xl")}>
            <HeroEyebrow
              ctx={ctx}
              node={node}
              className="mb-5 inline-flex -rotate-2 rounded-site-sm bg-site-secondary/30 px-3 py-1.5"
            />
            <HeroTitle
              ctx={ctx}
              node={node}
              className="text-[2.5rem] leading-[1.04] @md:text-5xl @5xl:text-[4rem]"
            />
            <HeroDescription
              ctx={ctx}
              node={node}
              className="mt-6 max-w-xl text-lg leading-relaxed text-site-muted"
            />
            <HeroActions ctx={ctx} node={node} />
            <Locale ctx={ctx} />
          </div>
          {hasImage ? (
            <div
              className="baze-enter-pop relative mx-auto w-full max-w-[18rem] @md:max-w-sm @4xl:max-w-none"
              style={enterDelay(2)}
            >
              <div
                aria-hidden="true"
                className="absolute -top-6 -left-4 size-28 rounded-full bg-site-secondary @3xl:-top-10 @3xl:-left-10 @3xl:size-40"
              />
              <div
                aria-hidden="true"
                className="absolute -right-3 -bottom-6 size-24 rotate-12 bg-site-accent @3xl:-right-8 @3xl:-bottom-8 @3xl:size-32"
              />
              <div
                aria-hidden="true"
                className="absolute top-1/2 -right-6 hidden h-0 w-0 border-y-[2.25rem] border-l-[3.5rem] border-y-transparent border-l-site-primary/80 @4xl:block"
              />
              <div className="relative -rotate-3 bg-white p-3 pb-14 shadow-site-lg transition-transform duration-500 hover:rotate-0 motion-reduce:transition-none @3xl:p-4 @3xl:pb-16">
                <span
                  aria-hidden="true"
                  className="absolute -top-3 left-6 h-7 w-24 -rotate-6 bg-site-secondary/70 mix-blend-multiply"
                />
                <span
                  aria-hidden="true"
                  className="absolute -top-3 right-6 h-7 w-20 rotate-6 bg-site-accent/60 mix-blend-multiply"
                />
                <div className="aspect-square overflow-hidden bg-site-surface @4xl:aspect-[4/5]">
                  {c.image ? (
                    <Img
                      ctx={ctx}
                      image={c.image}
                      sizes="(min-width: 1024px) 520px, 90vw"
                      priority={isPriority(ctx, node)}
                      path="content.image"
                      target={1200}
                    />
                  ) : (
                    <EditorImagePlaceholder ctx={ctx} path="content.image" />
                  )}
                </div>
              </div>
              <span
                aria-hidden="true"
                className="baze-wiggle absolute -bottom-6 -left-4 grid size-20 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md ring-4 ring-site-bg @3xl:size-24"
              >
                <Icon name="sparkle" className="size-8 @3xl:size-10" />
              </span>
            </div>
          ) : null}
        </div>
      </Container>
    </SectionShell>
  );
}

/**
 * Product shot on a lit stage: headline, then the photo in a frame with a colour slab behind it.
 * Two blurred fields drift on live sites (CSS only). The frame itself floats.
 */
function Stage({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="baze-drift absolute top-[-12%] left-[8%] size-[34rem] rounded-full bg-site-primary/25 blur-3xl" />
        <div className="baze-drift-late absolute top-[28%] right-[-8%] size-[26rem] rounded-full bg-site-secondary/50 blur-3xl" />
      </div>
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
          <HeroTitle
            ctx={ctx}
            node={node}
            className="text-[2.5rem] leading-[1.02] @md:text-6xl @3xl:text-7xl @5xl:text-[5rem]"
          />
          <HeroDescription
            ctx={ctx}
            node={node}
            className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl"
          />
          <HeroActions ctx={ctx} node={node} center />
          <Locale ctx={ctx} center />
        </div>
        {hasImage ? (
          <div className="baze-enter-pop relative mx-auto mt-12 max-w-4xl @3xl:mt-16" style={enterDelay(4)}>
            <div
              aria-hidden="true"
              className="absolute inset-x-6 top-8 -bottom-4 -z-10 rounded-site-lg bg-site-primary @3xl:inset-x-14"
            />
            <div className="baze-float overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg ring-1 ring-white/15">
              <div className="aspect-[4/3] @3xl:aspect-[16/9]">
                {c.image ? (
                  <Img
                    ctx={ctx}
                    image={c.image}
                    sizes="(min-width: 1024px) 896px, 100vw"
                    priority={isPriority(ctx, node)}
                    path="content.image"
                    target={1600}
                  />
                ) : (
                  <EditorImagePlaceholder ctx={ctx} path="content.image" />
                )}
              </div>
            </div>
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

/** Thick colour margin around a paper sheet: title on one side, one photo on the other. */
function Margin({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className="baze-hero baze-band relative bg-site-primary! text-site-on-primary"
    >
      <div className="p-3 @3xl:p-5 @5xl:p-6">
        <div className="rounded-site-md bg-site-surface px-6 py-12 text-site-text @3xl:px-12 @3xl:py-16 @5xl:px-16 @5xl:py-20">
          <div
            className={cx("grid items-end gap-10 @3xl:gap-14", hasImage && "@4xl:grid-cols-[1.15fr_0.85fr]")}
          >
            <div>
              <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
              <HeroTitle
                ctx={ctx}
                node={node}
                className="text-[2.6rem] leading-[0.96] @md:text-6xl @3xl:text-7xl @5xl:text-[5.25rem]"
              />
              <HeroDescription
                ctx={ctx}
                node={node}
                className="mt-6 max-w-xl text-lg leading-relaxed text-site-muted"
              />
              <HeroActions ctx={ctx} node={node} />
              <Locale ctx={ctx} />
            </div>
            {hasImage ? (
              <div
                className="baze-enter-pop overflow-hidden rounded-site-sm bg-site-bg"
                style={enterDelay(2)}
              >
                <div className="aspect-[4/5] @3xl:aspect-[5/6]">
                  {c.image ? (
                    <Img
                      ctx={ctx}
                      image={c.image}
                      sizes="(min-width: 1024px) 520px, 100vw"
                      priority={isPriority(ctx, node)}
                      path="content.image"
                      target={1400}
                    />
                  ) : (
                    <EditorImagePlaceholder ctx={ctx} path="content.image" />
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

// ── Invitation openings ─────────────────────────────────────────────────────────

/**
 * "Kepada Yth. … Tamu Undangan" card shared by invitation covers. The name is a placeholder that the
 * runtime fills from the link's `?to=` (personal links from the dashboard).
 */
function CoverGuest({
  ctx,
  light,
  className,
  delay = 3,
}: {
  ctx: RenderContext;
  light?: boolean;
  className?: string;
  delay?: number;
}) {
  const en = ctx.site.locale === "en-US";
  return (
    <div
      className={cx(
        "baze-enter mx-auto inline-flex min-w-64 flex-col items-center rounded-site-lg px-8 py-5 ring-1 backdrop-blur-sm",
        light ? "bg-white/10 ring-white/25" : "bg-site-surface/80 ring-site-border shadow-site-sm",
        className,
      )}
      style={enterDelay(delay)}
    >
      <span
        className={cx(
          "text-[0.6875rem] font-semibold tracking-[0.22em] uppercase",
          light ? "text-white/75" : "text-site-muted",
        )}
      >
        {en ? "Dear" : "Kepada Yth."}
      </span>
      <span className={cx("mt-1 text-sm", light ? "text-white/80" : "text-site-muted")}>
        {en ? "our honoured guest" : "Bapak/Ibu/Saudara/i"}
      </span>
      <span
        data-baze-guest=""
        className={cx(
          "baze-heading mt-2 max-w-full text-2xl font-semibold break-words",
          light ? "text-white" : "text-site-text",
        )}
      >
        {en ? "Guest" : "Tamu Undangan"}
      </span>
    </div>
  );
}

/** "Raka & Dinda" → ["Raka", "Dinda"]; one name (or none) → null. */
function coupleOf(title: string): [string, string] | null {
  const parts = title
    .split(/\s*(?:&|\+|\bdan\b|\band\b)\s*/i)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length === 2 ? [parts[0]!, parts[1]!] : null;
}

const initialOf = (name: string) => (name.match(/\p{L}/u)?.[0] ?? "").toUpperCase();
/** Three-letter "airport code" for a name: RAK, DIN. */
const codeOf = (name: string) =>
  (
    name
      .normalize("NFD")
      .replace(/[^A-Za-z]/g, "")
      .slice(0, 3)
      .toUpperCase() || "—"
  ).padEnd(3, "X");

/** The wedding day from the page's countdown, for covers that print it (ticket, masthead). */
function weddingDay(ctx: RenderContext) {
  const cd = ctx.page.sections.find((s) => s.type === "countdown" && !s.hidden) as
    { content: { date?: string; time?: string; timeNote?: string } } | undefined;
  const d = parseEventDate(cd?.content.date);
  return d ? { ...d, time: cd?.content.time, zone: cd?.content.timeNote ?? "WIB" } : null;
}

/**
 * Opening button: the primary CTA marked `data-baze-open`. On live sites the invitation script plays the
 * cover's opening (seal breaks, ticket tears, doors swing) and then follows the link; without the script
 * it is a plain anchor, so nothing is ever locked behind it.
 */
function OpenAction({ ctx, node, className }: { ctx: RenderContext; node: HeroNode; className?: string }) {
  const c = node.content;
  if (!ctaVisible(ctx, c.primaryCta)) return null;
  return (
    <div data-baze-open="" className={cx("baze-enter flex justify-center", className)} style={enterDelay(4)}>
      <CtaButton ctx={ctx} cta={c.primaryCta} path="content.primaryCta" style="primary" />
    </div>
  );
}

function Seal({ initials, className }: { initials: string; className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cx(
        "baze-seal relative grid size-20 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md @3xl:size-24",
        className,
      )}
    >
      <svg viewBox="0 0 100 100" className="absolute inset-0 size-full text-site-primary">
        <path
          fill="currentColor"
          d="M50 2c6 0 8 5 13 6s10-2 14 2 1 9 4 13 8 5 9 10-3 9-3 14 5 8 3 13-8 5-11 9-2 9-7 11-9-2-14-1-8 5-13 5-8-5-13-6-10 2-14-2-1-9-4-13-8-5-9-10 3-9 3-14-5-8-3-13 8-5 11-9 2-9 7-11 9 2 14 1 8-5 13-5Z"
        />
      </svg>
      <span className="absolute inset-2.5 rounded-full border border-current/35" />
      <span className="baze-heading relative text-xl font-semibold tracking-wider @3xl:text-2xl">
        {initials}
      </span>
    </div>
  );
}

/**
 * Keepsake envelope: an addressed envelope (couple names, the guest) closed with a wax seal. Opening it
 * breaks the seal, swings the flap up and slides the letter out before the page scrolls on.
 */
function Envelope({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const couple = coupleOf(c.title);
  const initials = couple ? `${initialOf(couple[0])}${initialOf(couple[1])}` : initialOf(c.title);
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className={cx(
        "baze-hero baze-band relative isolate flex items-center overflow-hidden py-16 text-center",
        ctx.editor || ctx.thumbnail ? "min-h-[44rem]" : "min-h-[100svh]",
      )}
    >
      <Glow />
      <Ambient kind={ambientOf(ctx.theme)} className="-z-10" />
      <Container size="narrow" className="relative">
        <div
          data-baze-gate=""
          className="baze-envelope relative mx-auto w-full max-w-xl [perspective:1400px]"
        >
          {/* The letter waits inside the envelope and rises out when it opens. */}
          <div
            aria-hidden="true"
            className="baze-letter absolute inset-x-6 top-6 bottom-10 rounded-site-md bg-site-bg shadow-site-md ring-1 ring-site-border"
          >
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6">
              <Ornament />
              {c.image ? (
                <div className="size-24 overflow-hidden rounded-full ring-4 ring-site-surface">
                  <Img ctx={ctx} image={c.image} sizes="96px" path="content.image" target={240} />
                </div>
              ) : null}
              <span className="baze-heading text-2xl text-site-text">{c.title}</span>
            </div>
          </div>
          <div
            className="baze-enter-pop relative rounded-site-lg bg-site-surface px-6 pt-16 pb-10 shadow-site-lg ring-1 ring-site-border @3xl:px-12 @3xl:pt-20"
            style={enterDelay(0)}
          >
            {/* Envelope flap: a triangle hinged at the top edge. */}
            <div
              aria-hidden="true"
              className="baze-flap absolute inset-x-0 top-0 h-[46%] origin-top [clip-path:polygon(0_0,100%_0,50%_100%)] bg-site-secondary/40 ring-1 ring-site-border"
            />
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-[46%] [clip-path:polygon(0_0,100%_0,50%_100%)] bg-linear-to-b from-site-secondary/30 to-site-surface/0"
            />
            <Seal initials={initials} className="baze-enter relative z-10 mx-auto -mt-4 mb-8" />
            <HeroEyebrow ctx={ctx} node={node} className="mb-4 tracking-[0.3em]" />
            <HeroTitle
              ctx={ctx}
              node={node}
              className="text-[2.5rem] leading-[1.05] @md:text-5xl @3xl:text-6xl"
            />
            <HeroDescription
              ctx={ctx}
              node={node}
              className="mx-auto mt-5 max-w-md leading-relaxed text-site-muted"
            />
            <CoverGuest ctx={ctx} className="mt-8" />
            <OpenAction ctx={ctx} node={node} className="mt-8" />
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/**
 * Boarding pass: the couple as a route (RAK → DIN), the wedding day as the flight date, the guest as the
 * passenger. Opening tears the stub off and a little plane takes off across the ticket.
 */
function BoardingPass({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const en = ctx.site.locale === "en-US";
  const couple = coupleOf(c.title);
  const day = weddingDay(ctx);
  const city = ctx.site.contact.city;
  const label = "text-[0.625rem] font-semibold tracking-[0.2em] text-site-muted uppercase";
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className={cx(
        "baze-hero baze-band relative isolate flex items-center overflow-hidden py-16",
        ctx.editor || ctx.thumbnail ? "min-h-[44rem]" : "min-h-[100svh]",
      )}
    >
      <Glow />
      <Ambient kind={ambientOf(ctx.theme)} className="-z-10" />
      <Container className="relative">
        <div className="mx-auto max-w-3xl text-center">
          <HeroEyebrow ctx={ctx} node={node} className="mb-4 tracking-[0.3em]" />
          <HeroTitle
            ctx={ctx}
            node={node}
            className="text-[2.5rem] leading-[1.05] @md:text-5xl @3xl:text-6xl"
          />
          <HeroDescription
            ctx={ctx}
            node={node}
            className="mx-auto mt-5 max-w-lg leading-relaxed text-site-muted"
          />
        </div>
        <div data-baze-gate="" className="baze-ticket relative mx-auto mt-10 max-w-3xl">
          <div
            className="baze-enter-pop flex flex-col overflow-hidden rounded-site-lg bg-site-surface text-left shadow-site-lg ring-1 ring-site-border @2xl:flex-row"
            style={enterDelay(2)}
          >
            <div className="relative flex-1 p-6 @3xl:p-8">
              <div className="flex items-center justify-between gap-3 border-b border-dashed border-site-border pb-4">
                <span className="baze-heading text-sm font-semibold tracking-[0.25em] text-site-primary uppercase">
                  Boarding pass
                </span>
                <span className={label}>{en ? "Wedding flight" : "Penerbangan cinta"}</span>
              </div>
              <div className="mt-6 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <span className="baze-heading block text-4xl font-semibold text-site-text @3xl:text-5xl">
                    {couple ? codeOf(couple[0]) : "LOV"}
                  </span>
                  <span className="mt-1 block truncate text-sm text-site-muted">
                    {couple?.[0] ?? c.title}
                  </span>
                </div>
                <div aria-hidden="true" className="relative flex flex-1 items-center text-site-primary">
                  <span className="h-px flex-1 border-t-2 border-dotted border-current/40" />
                  <svg viewBox="0 0 24 24" className="baze-plane mx-2 size-7 shrink-0" fill="currentColor">
                    <path
                      d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5Z"
                      transform="rotate(90 12 12)"
                    />
                  </svg>
                  <span className="h-px flex-1 border-t-2 border-dotted border-current/40" />
                </div>
                <div className="min-w-0 text-right">
                  <span className="baze-heading block text-4xl font-semibold text-site-text @3xl:text-5xl">
                    {couple ? codeOf(couple[1]) : "EVR"}
                  </span>
                  <span className="mt-1 block truncate text-sm text-site-muted">
                    {couple?.[1] ?? (en ? "Forever" : "Selamanya")}
                  </span>
                </div>
              </div>
              <dl className="mt-7 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <dt className={label}>{en ? "Date" : "Tanggal"}</dt>
                  <dd className="mt-1 font-semibold text-site-text">
                    {day ? `${day.day} ${day.monthName.slice(0, 3)} ${day.year}` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className={label}>Boarding</dt>
                  <dd className="mt-1 font-semibold text-site-text">
                    {day?.time ? `${day.time} ${day.zone}` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className={label}>{en ? "Gate" : "Gerbang"}</dt>
                  <dd className="mt-1 truncate font-semibold text-site-text">{city ?? "01"}</dd>
                </div>
              </dl>
              <div className="mt-6 border-t border-dashed border-site-border pt-4">
                <span className={label}>{en ? "Passenger" : "Penumpang"}</span>
                <span
                  data-baze-guest=""
                  className="baze-heading mt-1 block text-2xl font-semibold break-words text-site-text"
                >
                  {en ? "Guest" : "Tamu Undangan"}
                </span>
              </div>
            </div>
            {/* Stub: perforated edge, tears away when the invitation opens. */}
            <div className="baze-stub relative flex flex-col items-center justify-center gap-4 border-t-2 border-dashed border-site-border bg-site-primary/8 p-6 @2xl:w-52 @2xl:border-t-0 @2xl:border-l-2">
              <span
                aria-hidden="true"
                className="absolute -top-3 -left-3 size-6 rounded-full bg-site-bg @2xl:-top-3 @2xl:-left-3"
              />
              <span
                aria-hidden="true"
                className="absolute -top-3 -right-3 size-6 rounded-full bg-site-bg @2xl:top-auto @2xl:-bottom-3 @2xl:-left-3 @2xl:right-auto"
              />
              <span className={label}>{en ? "Seat" : "Kursi"}</span>
              <span className="baze-heading text-3xl font-semibold text-site-primary">VIP</span>
              <span
                aria-hidden="true"
                className="h-12 w-full max-w-36 bg-[repeating-linear-gradient(90deg,currentColor_0_2px,transparent_2px_4px,currentColor_4px_5px,transparent_5px_8px)] text-site-text/70"
              />
            </div>
          </div>
        </div>
        <OpenAction ctx={ctx} node={node} className="mt-8" />
      </Container>
    </SectionShell>
  );
}

/**
 * Retro newspaper front page: masthead, the couple as the headline, a halftone photo and the story in
 * columns. The page spins in like an old film's "extra! extra!" on live sites.
 */
function Newspaper({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const en = ctx.site.locale === "en-US";
  const day = weddingDay(ctx);
  const rule = "border-site-text";
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className="baze-hero baze-band relative isolate overflow-hidden py-10 @3xl:py-16"
    >
      <Container>
        <div
          className="baze-newspaper baze-enter-pop mx-auto max-w-4xl bg-site-surface px-5 py-6 text-site-text shadow-site-lg ring-1 ring-site-border @3xl:px-10 @3xl:py-8"
          style={enterDelay(0)}
        >
          <div
            className={cx(
              "flex items-center justify-between gap-3 border-b pb-2 text-[0.625rem] font-semibold tracking-[0.2em] uppercase @3xl:text-xs",
              rule,
            )}
          >
            <span>{en ? "Special edition" : "Edisi istimewa"}</span>
            <span className="hidden @md:inline">
              {ctx.site.contact.city ?? (en ? "Love Street" : "Jalan Cinta")}
            </span>
            <span>{day ? day.long : en ? "Wedding issue" : "Edisi pernikahan"}</span>
          </div>
          <div className={cx("border-b-4 border-double py-3 text-center", rule)}>
            <span className="baze-heading block text-3xl leading-none font-black tracking-tight uppercase @md:text-5xl @3xl:text-6xl">
              {en ? "The Wedding Times" : "Kabar Bahagia"}
            </span>
          </div>
          <div className="mt-5 text-center">
            <HeroEyebrow ctx={ctx} node={node} className="mb-2 text-site-text!" />
            <HeroTitle
              ctx={ctx}
              node={node}
              className="text-[2.6rem] leading-[0.95] font-black uppercase @md:text-6xl @3xl:text-7xl"
            />
          </div>
          <div className={cx("mt-6 grid gap-6 border-t pt-6 @3xl:grid-cols-[1.2fr_1fr]", rule)}>
            {c.image || ctx.editor ? (
              <figure className="baze-enter" style={enterDelay(2)}>
                <div className="baze-halftone aspect-[4/3] overflow-hidden bg-site-bg grayscale contrast-125">
                  {c.image ? (
                    <Img
                      ctx={ctx}
                      image={c.image}
                      sizes="(min-width: 1024px) 520px, 100vw"
                      priority={isPriority(ctx, node)}
                      path="content.image"
                      target={1200}
                    />
                  ) : (
                    <EditorImagePlaceholder ctx={ctx} path="content.image" />
                  )}
                </div>
                <figcaption className="mt-2 text-xs text-site-muted italic">
                  {en
                    ? "The happy couple, photographed by our correspondent."
                    : "Kedua mempelai, diabadikan oleh koresponden kami."}
                </figcaption>
              </figure>
            ) : null}
            <div className="text-left">
              <HeroDescription
                ctx={ctx}
                node={node}
                className="baze-heading text-center text-lg leading-relaxed italic"
              />
              <div className={cx("baze-enter mt-6 border-y py-4 text-center", rule)} style={enterDelay(3)}>
                <span className="text-[0.625rem] font-semibold tracking-[0.25em] uppercase">
                  {en ? "Printed especially for" : "Dicetak khusus untuk"}
                </span>
                <span data-baze-guest="" className="baze-heading mt-1 block text-2xl font-bold break-words">
                  {en ? "Guest" : "Tamu Undangan"}
                </span>
              </div>
              <OpenAction ctx={ctx} node={node} className="mt-6" />
            </div>
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/**
 * Glasshouse: a watercolour garden behind an arched greenhouse whose lattice draws itself; two glass
 * doors swing open onto the couple's photo, blooms open in the corners.
 */
function Glasshouse({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const pane =
    "absolute inset-y-0 w-1/2 bg-white/18 backdrop-blur-[1.5px] ring-1 ring-white/50 [background-image:linear-gradient(90deg,transparent_calc(50%-1px),rgba(255,255,255,0.7)_calc(50%-1px)_calc(50%+1px),transparent_calc(50%+1px)),linear-gradient(transparent_calc(33%-1px),rgba(255,255,255,0.7)_calc(33%-1px)_calc(33%+1px),transparent_calc(33%+1px)_calc(66%-1px),rgba(255,255,255,0.7)_calc(66%-1px)_calc(66%+1px),transparent_calc(66%+1px))]";
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className={cx(
        "baze-hero baze-band relative isolate flex items-center overflow-hidden py-16 text-center",
        ctx.editor || ctx.thumbnail ? "min-h-[44rem]" : "min-h-[100svh]",
      )}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="baze-bloom absolute -top-16 -left-16 size-72 rounded-full bg-site-secondary/45 blur-2xl" />
        <div
          className="baze-bloom absolute -right-20 -bottom-20 size-80 rounded-full bg-site-accent/40 blur-2xl"
          style={{ animationDelay: "0.6s" }}
        />
        <div
          className="baze-bloom absolute top-1/3 -right-10 size-40 rounded-full bg-site-primary/20 blur-2xl"
          style={{ animationDelay: "1.1s" }}
        />
      </div>
      <Ambient kind={ambientOf(ctx.theme)} className="-z-10" />
      <Container className="relative">
        <div className="grid items-center gap-10 @4xl:grid-cols-[1fr_1.1fr]">
          <div className="order-2 @4xl:order-1">
            <HeroEyebrow ctx={ctx} node={node} className="mb-4 tracking-[0.3em]" />
            <HeroTitle
              ctx={ctx}
              node={node}
              className="text-[2.75rem] leading-[1.02] @md:text-6xl @5xl:text-7xl"
            />
            <div className="baze-enter mt-5" style={enterDelay(2)}>
              <Ornament className="mx-auto" />
            </div>
            <HeroDescription
              ctx={ctx}
              node={node}
              className="mx-auto mt-5 max-w-md leading-relaxed text-site-muted"
            />
            <CoverGuest ctx={ctx} className="mt-8" />
            <OpenAction ctx={ctx} node={node} className="mt-8" />
          </div>
          <div
            data-baze-gate=""
            className="baze-glasshouse baze-enter-pop relative order-1 mx-auto w-full max-w-sm @4xl:order-2 @4xl:max-w-md"
            style={enterDelay(1)}
          >
            <div className="relative aspect-[3/4] overflow-hidden rounded-t-full bg-site-surface shadow-site-lg ring-4 ring-site-surface">
              {c.image ? (
                <Img
                  ctx={ctx}
                  image={c.image}
                  sizes="(min-width: 1024px) 448px, 90vw"
                  priority={isPriority(ctx, node)}
                  path="content.image"
                  target={1000}
                />
              ) : ctx.editor ? (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              ) : null}
              <div
                aria-hidden="true"
                className="baze-doors pointer-events-none absolute inset-0 [perspective:900px]"
              >
                <span className={cx(pane, "baze-door-l left-0 origin-left rounded-tl-full")} />
                <span className={cx(pane, "baze-door-r right-0 origin-right rounded-tr-full")} />
              </div>
            </div>
            {/* Greenhouse frame: an arch with lattice ribs that draw themselves in. */}
            <svg
              aria-hidden="true"
              viewBox="0 0 300 400"
              preserveAspectRatio="none"
              className="baze-lattice pointer-events-none absolute -inset-3 h-[calc(100%+1.5rem)] w-[calc(100%+1.5rem)] text-site-primary"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path pathLength={1} d="M6 398V150A144 144 0 0 1 294 150V398" />
              <path
                pathLength={1}
                d="M150 6V398M6 150H294M6 270H294M48 48l102 102M252 48 150 150"
                strokeWidth="1.2"
                opacity="0.6"
              />
            </svg>
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

export function Hero(props: SectionProps<"hero">) {
  switch (props.node.variant) {
    case "margin":
      return <Margin {...props} />;
    case "stage":
      return <Stage {...props} />;
    case "marquee":
      return <Marquee {...props} />;
    case "collage":
      return <Collage {...props} />;
    case "cover":
      return <Cover {...props} />;
    case "envelope":
      return <Envelope {...props} />;
    case "boarding-pass":
      return <BoardingPass {...props} />;
    case "newspaper":
      return <Newspaper {...props} />;
    case "glasshouse":
      return <Glasshouse {...props} />;
    case "celebration":
      return <Celebration {...props} />;
    case "profile":
      return <Profile {...props} />;
    case "centered":
      return <Centered {...props} />;
    case "image-background":
      return <ImageBackground {...props} />;
    case "slideshow":
      return <Slideshow {...props} />;
    case "minimal":
      return <Minimal {...props} />;
    case "arch":
      return <Arch {...props} />;
    case "editorial":
      return <Editorial {...props} />;
    default:
      return <Split {...props} />;
  }
}
